import ReviewTrustBadge from '@/components/reviews/ReviewTrustBadge';
import React from 'react';
import { Award, Phone, ArrowRight, ShieldCheck } from 'lucide-react';
// No framer-motion here: every wrapper used `initial={false}`, which renders
// the final state immediately (no animation), so plain elements are
// identical on screen and keep the ~100 KB (compressed) library out of the
// entry bundle that gates the largest contentful paint.
import PatientQuiz from '@/components/trinity/PatientQuiz';

export default function Hero() {

  return (
    <section id="main-content" className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-amber-50/30 py-6 md:py-8 flex items-center" aria-label="Hero">
      <div className="hidden md:block absolute top-20 right-0 w-[600px] h-[600px] bg-amber-200/30 rounded-full blur-3xl" />
      <div className="hidden md:block absolute bottom-0 left-0 w-[500px] h-[500px] bg-slate-200/40 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 md:px-6 lg:px-12 relative z-10 py-4 md:py-6">
        <div className="grid lg:grid-cols-2 gap-10 md:gap-16 items-start min-w-0">

          {/* Left: Headline + Stats */}
          <div>
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200/50 rounded-full">
                <Award className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-800">Implant & Cosmetic Dentistry · Trinity, FL</span>
              </div>
              <div className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-full shadow-sm">
                <ReviewTrustBadge />
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-full">
                <ShieldCheck className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-medium">All-on-X Full-Arch Implants</span>
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-full">
                <Award className="w-4 h-4 text-amber-100" />
                <span className="text-sm font-medium">Board-Certified Implantologist</span>
              </div>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl xl:text-7xl font-light text-slate-800 leading-tight mb-3 md:mb-4">
              Dental Implants Planned for <span className="font-serif italic text-amber-700">Your Bite and Smile</span>
            </h1>

            <p className="text-base md:text-lg text-slate-600 mb-6 leading-relaxed">
              <a href="/our-results" className="font-semibold text-amber-800 underline underline-offset-4">See results from real Trinity Dental Arts patients.</a>{' '}
              Dr. Xhoana Gjelaj is board-certified in implant dentistry through the ABOI/ID. She reviews your oral health, bite and goals before recommending treatment. Our in-house lab supports restoration design, and sedation is planned around your health and treatment.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="#consultation-quiz"
                className="group inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold text-base px-8 py-4 rounded-full shadow-lg shadow-amber-600/30 hover:shadow-xl hover:shadow-amber-600/40 transition-all"
              >
                Reserve your consultation
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="tel:+17272286846"
                aria-label="Call (727) 228-6846"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-slate-800 font-semibold text-base px-6 py-4 rounded-full border border-slate-200 shadow-sm transition-colors"
              >
                <Phone className="w-5 h-5 text-amber-600" />
                Call (727) 228-6846
              </a>
            </div>

            <p className="mt-4 text-sm font-medium text-slate-700">
              The $300 Signature Consultation includes a 90-minute exam with Dr. Gjelaj, CBCT, X-rays, an intraoral scan and a written treatment plan. The fee is credited toward treatment begun within 30 days.
            </p>
            <p className="mt-2 text-xs text-slate-500">
              No referral needed · Flexible financing · Doctor-owned care
            </p>

            {/* Proof row */}
            <div className="grid grid-cols-3 gap-4 mt-8">
              <div className="text-center lg:text-left">
                <div className="text-2xl font-light text-amber-600 mb-0.5">In-House</div>
                <div className="text-xs text-slate-500">Dental Laboratory</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-2xl font-light text-amber-600 mb-0.5">3D</div>
                <div className="text-xs text-slate-500">Imaging &amp; Planning</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-2xl font-light text-amber-600 mb-0.5">Written</div>
                <div className="text-xs text-slate-500">Individual Treatment Plans</div>
              </div>
            </div>

          </div>

          {/* Right: Patient Quiz */}
          <div id="consultation-quiz" className="scroll-mt-28">
            <PatientQuiz source="hero-quiz" />
          </div>

        </div>
      </div>
    </section>
  );
}